import React, { useState, useEffect, useCallback } from 'react';
import { inquiryService } from '../api/inquiryService';
import Pagination from '../components/Common/Pagination';
import '../components/Common/Common.css';

const STATUS_META = {
  pending:   { label: 'En attente', color: '#856404', bg: '#fff3cd' },
  contacted: { label: 'Contacté',   color: '#0a3a82', bg: '#cfe2ff' },
  processed: { label: 'Traité',     color: '#155724', bg: '#d4edda' },
  rejected:  { label: 'Rejeté',     color: '#721c24', bg: '#f8d7da' },
};

const STATUS_OPTIONS = ['pending', 'contacted', 'processed', 'rejected'];

const BUSINESS_LABEL = {
  transport:    'Transport & Logistique',
  delivery:     'Livraison & Distribution',
  construction: 'BTP & Construction',
  taxi_vtc:     'Taxi / VTC',
  rental:       'Location de véhicules',
  reseller:     'Revendeur / Installateur',
  company:      'Entreprise (flotte interne)',
  other:        'Autre',
};

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return iso;
  }
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status, color: '#333', bg: '#eee' };
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 99,
      fontSize: 12, fontWeight: 600, color: meta.color, background: meta.bg,
    }}>
      {meta.label}
    </span>
  );
}

function Inquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [actionId, setActionId] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [notesDraft, setNotesDraft] = useState({}); // { [id]: 'text' }

  const fetchInquiries = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: currentPage };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      const data = await inquiryService.list(params);
      setInquiries(data.data || []);
      setLastPage(data.last_page || 1);
    } catch (err) {
      setError(err.response?.data?.message || 'Échec du chargement des demandes');
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, search]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await inquiryService.statistics();
      setStats(data);
    } catch (_) {
      // non-blocking
    }
  }, []);

  useEffect(() => { fetchInquiries(); }, [fetchInquiries]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearch(searchInput.trim());
  };

  const handleStatusChange = async (id, newStatus) => {
    setActionId(id);
    setError('');
    setMessage('');
    try {
      await inquiryService.update(id, { status: newStatus });
      setMessage('Statut mis à jour');
      setTimeout(() => setMessage(''), 2000);
      fetchInquiries();
      fetchStats();
    } catch (err) {
      setError(err.response?.data?.message || 'Échec de la mise à jour');
    } finally {
      setActionId(null);
    }
  };

  const handleSaveNotes = async (id) => {
    setActionId(id);
    setError('');
    setMessage('');
    try {
      await inquiryService.update(id, { notes: notesDraft[id] ?? '' });
      setMessage('Notes enregistrées');
      setTimeout(() => setMessage(''), 2000);
      fetchInquiries();
    } catch (err) {
      setError(err.response?.data?.message || "Échec de l'enregistrement des notes");
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer définitivement cette demande ?')) return;
    setActionId(id);
    setError('');
    setMessage('');
    try {
      await inquiryService.remove(id);
      setMessage('Demande supprimée');
      setTimeout(() => setMessage(''), 2000);
      fetchInquiries();
      fetchStats();
    } catch (err) {
      setError(err.response?.data?.message || 'Échec de la suppression');
    } finally {
      setActionId(null);
    }
  };

  const toggleExpand = (inq) => {
    if (expandedId === inq.id) {
      setExpandedId(null);
    } else {
      setExpandedId(inq.id);
      setNotesDraft((prev) => ({ ...prev, [inq.id]: inq.notes || '' }));
    }
  };

  const StatPill = ({ label, value, color }) => (
    <div style={{
      flex: 1, minWidth: 130, padding: '12px 14px', background: '#fff',
      border: '1px solid #e2e8f0', borderRadius: 8,
    }}>
      <div style={{ fontSize: 12, color: '#666' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || '#222' }}>{value ?? '—'}</div>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <h1>Demandes B2B</h1>
      </div>

      {message && (
        <div style={{ marginBottom: 16, padding: 12, background: '#d4edda', color: '#155724', borderRadius: 6 }}>
          {message}
        </div>
      )}
      {error && (
        <div style={{ marginBottom: 16, padding: 12, background: '#f8d7da', color: '#721c24', borderRadius: 6 }}>
          {error}
        </div>
      )}

      {stats && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <StatPill label="Total" value={stats.total} />
          <StatPill label="En attente" value={stats.pending} color="#856404" />
          <StatPill label="Contactés" value={stats.contacted} color="#0a3a82" />
          <StatPill label="Traités" value={stats.processed} color="#155724" />
          <StatPill label="Rejetés" value={stats.rejected} color="#721c24" />
          <StatPill label="Aujourd'hui" value={stats.today} />
          <StatPill label="Ce mois" value={stats.this_month} />
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <select
          value={statusFilter}
          onChange={(e) => { setCurrentPage(1); setStatusFilter(e.target.value); }}
          style={{ padding: '8px 12px', border: '1px solid #ced4da', borderRadius: 6, background: '#fff' }}
        >
          <option value="all">Tous les statuts</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{STATUS_META[s].label}</option>
          ))}
        </select>

        <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: 8, flex: 1, minWidth: 240 }}>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Rechercher entreprise, contact, email, téléphone, ville…"
            style={{ flex: 1, padding: '8px 12px', border: '1px solid #ced4da', borderRadius: 6 }}
          />
          <button
            type="submit"
            style={{ padding: '8px 16px', background: '#0d6efd', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}
          >
            Rechercher
          </button>
          {search && (
            <button
              type="button"
              onClick={() => { setSearch(''); setSearchInput(''); setCurrentPage(1); }}
              style={{ padding: '8px 12px', background: '#6c757d', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}
            >
              Effacer
            </button>
          )}
        </form>
      </div>

      {loading ? (
        <div className="loading">Chargement…</div>
      ) : inquiries.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', color: '#666' }}>
          Aucune demande pour le moment.
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.2fr 1fr 1fr 0.8fr 1.1fr 0.5fr', gap: 0, padding: '10px 14px', background: '#f8f9fa', borderBottom: '1px solid #e2e8f0', fontSize: 12, fontWeight: 600, color: '#444' }}>
            <div>Entreprise / Contact</div>
            <div>Email / Téléphone</div>
            <div>Ville</div>
            <div>Activité</div>
            <div>Statut</div>
            <div>Reçu le</div>
            <div></div>
          </div>

          {inquiries.map((inq) => {
            const isOpen = expandedId === inq.id;
            const busyHere = actionId === inq.id;
            return (
              <div key={inq.id} style={{ borderBottom: '1px solid #f1f3f5' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.2fr 1fr 1fr 0.8fr 1.1fr 0.5fr', gap: 0, padding: '12px 14px', alignItems: 'center', fontSize: 13 }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{inq.company_name}</div>
                    <div style={{ color: '#666' }}>{inq.contact_name}</div>
                  </div>
                  <div>
                    <a href={`mailto:${inq.email}`} style={{ color: '#0d6efd', display: 'block', textDecoration: 'none' }}>{inq.email}</a>
                    <a href={`tel:${inq.phone}`} style={{ color: '#666', textDecoration: 'none' }}>{inq.phone}</a>
                  </div>
                  <div>{inq.city || '—'}</div>
                  <div>{BUSINESS_LABEL[inq.business_type] || inq.business_type || '—'}</div>
                  <div><StatusBadge status={inq.status} /></div>
                  <div style={{ color: '#666', fontSize: 12 }}>{formatDate(inq.created_at)}</div>
                  <div style={{ textAlign: 'right' }}>
                    <button
                      type="button"
                      onClick={() => toggleExpand(inq)}
                      style={{ padding: '6px 10px', background: isOpen ? '#0a3a82' : '#0d6efd', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}
                    >
                      {isOpen ? 'Fermer' : 'Détails'}
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <div style={{ padding: '14px 18px 20px', background: '#fafbfc', borderTop: '1px solid #eef0f2' }}>
                    {inq.message && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>Message du prospect</div>
                        <div style={{ padding: 12, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, whiteSpace: 'pre-wrap', fontSize: 13 }}>
                          {inq.message}
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: 14 }}>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 6 }}>Statut</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {STATUS_OPTIONS.map((s) => {
                            const active = inq.status === s;
                            const meta = STATUS_META[s];
                            return (
                              <button
                                key={s}
                                type="button"
                                disabled={busyHere || active}
                                onClick={() => handleStatusChange(inq.id, s)}
                                style={{
                                  padding: '6px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600,
                                  border: '1px solid ' + (active ? meta.color : '#ced4da'),
                                  background: active ? meta.bg : '#fff',
                                  color: active ? meta.color : '#444',
                                  cursor: active ? 'default' : 'pointer',
                                  opacity: busyHere ? 0.6 : 1,
                                }}
                              >
                                {meta.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>Notes internes</div>
                      <textarea
                        rows="3"
                        value={notesDraft[inq.id] ?? ''}
                        onChange={(e) => setNotesDraft((prev) => ({ ...prev, [inq.id]: e.target.value }))}
                        placeholder="Ajoutez des notes pour votre équipe commerciale…"
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: 6, resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }}
                      />
                    </div>

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={() => handleSaveNotes(inq.id)}
                        disabled={busyHere}
                        style={{ padding: '8px 16px', background: '#0d6efd', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}
                      >
                        {busyHere ? 'Enregistrement…' : 'Enregistrer les notes'}
                      </button>
                      <a
                        href={`mailto:${inq.email}?subject=Re: votre demande B2B - ${encodeURIComponent(inq.company_name)}`}
                        style={{ padding: '8px 16px', background: '#198754', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, textDecoration: 'none' }}
                      >
                        Répondre par email
                      </a>
                      <a
                        href={`https://wa.me/${(inq.phone || '').replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ padding: '8px 16px', background: '#25D366', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, textDecoration: 'none' }}
                      >
                        WhatsApp
                      </a>
                      <button
                        type="button"
                        onClick={() => handleDelete(inq.id)}
                        disabled={busyHere}
                        style={{ marginLeft: 'auto', padding: '8px 16px', background: '#dc3545', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {lastPage > 1 && (
        <div style={{ marginTop: 16 }}>
          <Pagination
            currentPage={currentPage}
            lastPage={lastPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
}

export default Inquiries;
